import { useCallback } from "react";
import { useParams } from "react-router-dom";

import { type FetchResult, useMutation } from "@apollo/client";

import { type AbsoluteRoute, type RouteParams } from "../../core/routes";
import { type MyLensesLensCta } from "../../gql-generated/types";
import { type ApolloResult } from "../../shared/models";
import { type SetLensCtaMutation, type SetLensCtaMutationVariables, setLensCtaData } from "../api/mutations";
import { getLensInfo } from "../api/queries";

import { makeLensCtaInput } from "./makeLensCtaInput";

export const useSetLensCta = (): ApolloResult<{
    sendLensCta: (lensCta: MyLensesLensCta) => Promise<FetchResult<SetLensCtaMutation>>;
}> => {
    const { lensId } = useParams<RouteParams[AbsoluteRoute.LENS]>();

    const [setLensCtaInfo, { loading, error }] = useMutation<SetLensCtaMutation, SetLensCtaMutationVariables>(
        setLensCtaData,
        {
            refetchQueries: lensId ? [{ query: getLensInfo, variables: { lensId } }] : [],
        },
    );

    const sendLensCta = useCallback(
        (lensCta: MyLensesLensCta) => {
            if (!lensId) {
                throw new Error("Cannot set lens CTA. Missing Lens ID.");
            }
            // needed since LensCta has __typename prop
            // need to send mutation without it
            const lensCtaInput = makeLensCtaInput(lensCta);

            return setLensCtaInfo({
                variables: {
                    lensId,
                    lensCta: lensCtaInput,
                },
            });
        },
        [lensId, setLensCtaInfo],
    );

    return { sendLensCta, loading, error };
};
